import React from 'react';

import { Card } from '@copilot/ui';
import Badge from '../common/Badge';
import { formatAdminDate, formatAdminDuration } from './adminFormatting';

// The account's one-time signup trial (GET /api/admin/users/{id} ->
// free_trial; backend services/trial_service.py). Shown BESIDE the
// subscription, never as one: it is a one-time entitlement granted at
// signup - 2 meetings, each with its own time limit - and it never renews.
// Read-only; nothing here changes the trial.

const STATE_LABELS = {
  available: 'Available',
  in_progress: 'In Progress',
  exhausted: 'Exhausted',
  not_granted: 'Not granted',
};

const STATE_BADGES = {
  available: 'listening',
  in_progress: 'paused',
  exhausted: 'neutral',
  not_granted: 'neutral',
};

const SESSION_LABELS = {
  pending: 'Created, not started',
  active: 'In progress',
  completed: 'Completed',
  expired: 'Ended at time limit',
  cancelled: 'Never started (not counted)',
};

function FreeTrialCard({ trial }) {
  if (!trial) return null;

  const minutes = trial.meeting_duration_minutes || 0;
  const sessions = trial.sessions || [];

  return (
    <Card interactive title="Free Trial (one-time)" className="admin-section-heading">
      <div className="admin-info-list" data-testid="admin-free-trial">
        <div className="admin-info-row">
          <span className="admin-info-row-label">Type</span>
          <span className="admin-info-row-value">
            One-time signup benefit · not a monthly plan · does not renew
          </span>
        </div>
        <div className="admin-info-row">
          <span className="admin-info-row-label">Trial state</span>
          <span className="admin-info-row-value">
            <Badge variant={STATE_BADGES[trial.state] || 'neutral'}>
              {STATE_LABELS[trial.state] || trial.state}
            </Badge>
          </span>
        </div>
        {trial.eligible ? (
          <>
            <div className="admin-info-row">
              <span className="admin-info-row-label">Trial meetings granted</span>
              <span className="admin-info-row-value">{trial.total}</span>
            </div>
            <div className="admin-info-row">
              <span className="admin-info-row-label">Trial meetings used</span>
              <span className="admin-info-row-value">{trial.used}</span>
            </div>
            <div className="admin-info-row">
              <span className="admin-info-row-label">Trial meetings remaining</span>
              <span className="admin-info-row-value">{trial.remaining}</span>
            </div>
            <div className="admin-info-row">
              <span className="admin-info-row-label">Duration limit per meeting</span>
              <span className="admin-info-row-value">
                {minutes} {minutes === 1 ? 'minute' : 'minutes'}
              </span>
            </div>
            <div className="admin-info-row">
              <span className="admin-info-row-label">Granted</span>
              <span className="admin-info-row-value">{formatAdminDate(trial.granted_at)}</span>
            </div>
          </>
        ) : (
          <p className="admin-action-status">
            This account has no signup trial (it was created before the trial existed, or while it
            was switched off). Its plans and passes are unaffected.
          </p>
        )}

        {sessions.length > 0 && (
          <div className="admin-recent-meetings">
            {sessions.map((session) => (
              <div className="admin-recent-meeting-row" key={session.id}>
                <span>
                  <span className="admin-recent-meeting-title">
                    {session.sequence_number
                      ? `Free Meeting ${session.sequence_number} of ${trial.total}`
                      : 'Free meeting (not started)'}
                  </span>
                  <span className="admin-recent-meeting-meta">
                    {session.started_at ? formatAdminDate(session.started_at) : formatAdminDate(session.created_at)}
                    {session.started_at && session.ended_at
                      ? ` · ran ${formatAdminDuration(
                          Math.max(
                            0,
                            Math.round(
                              (new Date(session.ended_at).getTime() - new Date(session.started_at).getTime()) / 1000,
                            ),
                          ),
                          '0 min',
                        )}`
                      : ''}
                    {` · limit ${formatAdminDuration(session.duration_limit_seconds, '0 min')}`}
                  </span>
                </span>
                <Badge variant={session.status === 'active' ? 'paused' : 'neutral'}>
                  {SESSION_LABELS[session.status] || session.status}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export default FreeTrialCard;
